import { useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { SignIn, useAuth as useClerkAuth, useClerk, useUser } from "@clerk/clerk-react";
import { AuthSplash } from "@/components/layout/AuthSplash";
import { GeoKerjaLogo } from "@/components/GeoKerjaLogo";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useAuth } from "@/auth/AuthContext";
import type { AccountProblem } from "@/auth/account-problem";
import { ShieldCheck, MapPin, Activity, Building2, AlertTriangle, Ban, UserX } from "lucide-react";

/**
 * Sign-in page. The login *form* is now Clerk's prebuilt `<SignIn />`
 * component — handles email + password, Google OAuth, MFA prompt,
 * password recovery, all out of the box. We just keep the brand-side
 * panel for visual continuity.
 *
 * Three render states:
 *   1. Clerk loading            → splash
 *   2. Clerk says signed-in AND backend says we have a linked user
 *      → navigate to wherever they came from
 *   3. Clerk says signed-in BUT backend rejected (`linkError`)
 *      → say why, in words the person can act on (BUG-2026-018, BR-014),
 *        with Sign out. Crucially, we DO NOT <Navigate> in this case — the
 *        protected layouts would bounce right back here and we'd loop.
 *   4. Clerk says signed-out    → render `<SignIn />`
 */
const Login = () => {
  const { isLoaded, isSignedIn } = useClerkAuth();
  const { user, employee, loading: profileLoading, linkError } = useAuth();
  const location = useLocation();
  const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname ?? "/";

  if (!isLoaded) return <AuthSplash />;

  // Signed in via Clerk: wait until the backend profile resolves before
  // deciding whether to redirect or show the link error.
  if (isSignedIn) {
    if (profileLoading) return <AuthSplash />;
    if (user) return <Navigate to={from === "/login" ? "/" : from} replace />;
    // No admin row, but a linked employee record (FEAT-2026-013). Send them to
    // the employee lane. Checked after `user` so a dual-role human keeps
    // landing in the admin portal, which is what they signed in for; each
    // lane's header carries a link to the other.
    if (employee) {
      return <Navigate to={from.startsWith("/me") ? from : "/me"} replace />;
    }
    // Without a concrete linkError we're in a transitional state (e.g.
    // mid-signOut, where Clerk still reports `isSignedIn=true` for a
    // beat after our local user has been cleared). Show the splash
    // instead of flashing a misleading "not linked" message.
    if (!linkError) return <AuthSplash />;
    // Signed in by Clerk, but GeoKerja has no way in for them (BR-014).
    return <AccountProblemScreen problem={linkError} />;
  }

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      {/* Left: Brand panel — unchanged from the legacy login. */}
      <div className="relative hidden lg:flex flex-col justify-between overflow-hidden bg-gradient-hero p-10 text-white">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 20%, hsl(199 89% 48% / 0.4) 0, transparent 40%), radial-gradient(circle at 80% 70%, hsl(217 91% 60% / 0.3) 0, transparent 45%)",
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "linear-gradient(hsl(0 0% 100% / 0.04) 1px, transparent 1px), linear-gradient(90deg, hsl(0 0% 100% / 0.04) 1px, transparent 1px)",
            backgroundSize: "32px 32px",
          }}
        />

        <div className="relative">
          <GeoKerjaLogo variant="light" />
        </div>

        <div className="relative space-y-8">
          <div>
            <h2 className="font-display text-4xl font-bold leading-tight tracking-tight">
              Workforce intelligence,
              <br />
              everywhere your teams go.
            </h2>
            <p className="mt-3 max-w-md text-base text-white/80">
              GeoKerja is the command center for ground operations — attendance, sites, requests,
              and rostering, in one place.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4 max-w-md">
            <Feature icon={ShieldCheck} label="Compliance-grade audit trails" />
            <Feature icon={MapPin} label="Geofenced attendance" />
            <Feature icon={Activity} label="Live operational pulse" />
            <Feature icon={Building2} label="Multi-site rostering" />
          </div>
        </div>

        <div className="relative text-xs text-white/60">© {new Date().getFullYear()} NonPlus Sdn Bhd</div>
      </div>

      {/* Right: Clerk sign-in. */}
      <div className="flex min-h-screen items-center justify-center bg-background p-6 lg:p-12">
        <SignIn
          // Use path-based routing so deep links to MFA / recovery flows
          // stay within /login, not Clerk's hosted domain.
          path="/login"
          routing="path"
          // After a successful sign-in, return to wherever the protected
          // route wrapper bounced from.
          fallbackRedirectUrl={from === "/login" ? "/" : from}
          // Branded look — Clerk picks up the rest from our CSS vars.
          appearance={{
            elements: {
              rootBox: "w-full",
              card: "shadow-lg border border-border",
              // No "Don't have an account? Sign up": GeoKerja accounts are
              // invitation-only (BR-014). The production Clerk instance should
              // restrict sign-ups itself (deployment runbook); until it does,
              // the link led to an account GeoKerja cannot let in
              // (BUG-2026-018).
              footerAction: "hidden",
            },
          }}
        />
      </div>
    </div>
  );
};

/**
 * Someone Clerk has signed in whom GeoKerja cannot let in, told in words they
 * can act on (BUG-2026-018, BR-014). This was a page written for developers —
 * "This Clerk account isn't linked to a GeoKerja admin user … run
 * `bun run clerk:backfill`" — and it is where QA landed after signing up
 * through a "Sign up" link the portal should never have offered.
 */
function AccountProblemScreen({ problem }: { problem: AccountProblem }) {
  const clerk = useClerk();
  const { user } = useUser();
  const { refreshMe } = useAuth();
  const [retrying, setRetrying] = useState(false);
  const email = user?.primaryEmailAddress?.emailAddress ?? null;

  const retry = async () => {
    setRetrying(true);
    try {
      await refreshMe();
    } finally {
      setRetrying(false);
    }
  };

  const view = {
    "not-linked": {
      icon: UserX,
      title: "There's no GeoKerja account for this email",
      body: `You're signed in as ${email ?? "this email"}, but your organisation hasn't set up a GeoKerja account for it. GeoKerja accounts are created by your organisation's HR or admin team, who then email you an invitation — ask them to invite ${email ?? "this address"}, or sign out and use the address they invited.`,
    },
    inactive: {
      icon: Ban,
      title: "Your GeoKerja account is deactivated",
      body: `You're signed in as ${email ?? "this email"}, but your organisation has deactivated this account. If you think that's a mistake, contact your HR or admin team.`,
    },
    unavailable: {
      icon: AlertTriangle,
      title: "We couldn't open your GeoKerja account",
      body: "Something went wrong while loading your account. Check your connection and try again.",
    },
  }[problem.kind];
  const Icon = view.icon;

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <div className="w-full max-w-md space-y-4">
        <div className="flex justify-center"><GeoKerjaLogo /></div>
        <Alert variant={problem.kind === "unavailable" ? "destructive" : "default"}>
          <Icon className="h-4 w-4" />
          <AlertTitle>{view.title}</AlertTitle>
          <AlertDescription className="mt-2 text-sm">{view.body}</AlertDescription>
        </Alert>
        {problem.kind === "unavailable" && (
          <Button className="w-full" onClick={() => void retry()} disabled={retrying}>
            Try again
          </Button>
        )}
        <Button className="w-full" variant="outline" onClick={() => void clerk.signOut()}>
          Sign out
        </Button>
      </div>
    </div>
  );
}

const Feature = ({
  icon: Icon,
  label,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
}) => (
  <div className="flex items-start gap-2.5">
    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/10 backdrop-blur">
      <Icon className="h-4 w-4 text-white" />
    </div>
    <p className="text-sm text-white/85 leading-snug">{label}</p>
  </div>
);

export default Login;
